import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Crown, ArrowLeft, Calendar, FileText, Star, Users, TrendingUp,
  Clock, Check, X, MessageSquare, Phone, Mail, QrCode, Link2,
  Download, Copy, ExternalLink, Settings, ChevronRight, Loader2,
  DollarSign, Eye, BarChart3, Bell, Filter
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { getPalette } from '../data/portfolioMock';

const ProPainel = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const qrRef = useRef(null);
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [appointments, setAppointments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const publicUrl = `${window.location.origin}/pro/${slug}`;
  const shortUrl = `ativaja.com/${slug}`;

  useEffect(() => {
    loadData();
  }, [slug]);

  const loadData = async () => {
    try {
      const [profRes, apptRes, quotesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/pro/professionals/${slug}`),
        fetch(`${API_URL}/api/pro/professionals/${slug}/appointments`),
        fetch(`${API_URL}/api/pro/professionals/${slug}/quotes`),
        fetch(`${API_URL}/api/pro/professionals/${slug}/stats`)
      ]);

      if (profRes.ok) setProfessional(await profRes.json());
      if (apptRes.ok) setAppointments(await apptRes.json());
      if (quotesRes.ok) setQuotes(await quotesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) {
      console.error('Error loading data:', e);
    }
    setLoading(false);
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    
    img.onload = () => {
      canvas.width = 512;
      canvas.height = 512;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      
      const link = document.createElement('a');
      link.download = `qrcode-${slug}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const colors = professional ? getPalette(professional.colorPalette) : { primary: '#f59e0b' };

  // Calculate monthly stats
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  
  const monthlyAppointments = appointments.filter(a => {
    const date = new Date(a.created_at || a.date);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  });

  const monthlyQuotes = quotes.filter(q => {
    const date = new Date(q.created_at);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  });

  const monthlyRevenue = monthlyAppointments
    .filter(a => a.status === 'completed')
    .reduce((sum, a) => sum + (parseFloat(a.service?.price) || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-950">
        <Crown size={48} className="text-slate-600 mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Profissional não encontrado</h1>
        <Button onClick={() => navigate('/pro/configurar')} className="bg-amber-500">
          Criar conta Pro
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/pro')} className="text-slate-400 hover:text-white">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Crown className="text-amber-400" size={20} />
              <span className="font-bold text-white">{professional.business}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open(`/pro/${slug}`, '_blank')}
              className="border-slate-700 text-slate-300"
            >
              <Eye size={16} />
              Ver página
            </Button>
            <Button 
              size="sm"
              onClick={() => setShowQRModal(true)}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <QrCode size={16} />
              QR Code
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-slate-900/50 border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
              { id: 'appointments', label: 'Agendamentos', icon: Calendar },
              { id: 'quotes', label: 'Orçamentos', icon: FileText },
              { id: 'share', label: 'Compartilhar', icon: Link2 },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-amber-500 text-amber-400'
                    : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                <tab.icon size={18} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Calendar className="text-blue-400" size={20} />
                  </div>
                  <span className="text-slate-400 text-sm">Agendamentos</span>
                </div>
                <p className="text-2xl font-bold text-white">{monthlyAppointments.length}</p>
                <p className="text-xs text-slate-500">Este mês</p>
              </div>

              <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <FileText className="text-purple-400" size={20} />
                  </div>
                  <span className="text-slate-400 text-sm">Orçamentos</span>
                </div>
                <p className="text-2xl font-bold text-white">{monthlyQuotes.length}</p>
                <p className="text-xs text-slate-500">Este mês</p>
              </div>

              <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <DollarSign className="text-green-400" size={20} />
                  </div>
                  <span className="text-slate-400 text-sm">Faturamento</span>
                </div>
                <p className="text-2xl font-bold text-white">R$ {monthlyRevenue.toFixed(0)}</p>
                <p className="text-xs text-slate-500">Este mês</p>
              </div>

              <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Star className="text-amber-400" size={20} />
                  </div>
                  <span className="text-slate-400 text-sm">Avaliação</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats?.average_rating || '5.0'}</p>
                <p className="text-xs text-slate-500">{stats?.total_testimonials || 0} avaliações</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
              <h3 className="font-semibold text-white mb-4">Ações Rápidas</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <button 
                  onClick={() => setShowQRModal(true)}
                  className="flex items-center gap-3 p-4 bg-slate-800 rounded-lg hover:bg-slate-700 transition-all"
                >
                  <QrCode className="text-amber-400" size={24} />
                  <div className="text-left">
                    <p className="text-white font-medium">QR Code</p>
                    <p className="text-xs text-slate-400">Para cartão de visita</p>
                  </div>
                </button>
                
                <button 
                  onClick={() => copyLink(publicUrl)}
                  className="flex items-center gap-3 p-4 bg-slate-800 rounded-lg hover:bg-slate-700 transition-all"
                >
                  <Link2 className="text-blue-400" size={24} />
                  <div className="text-left">
                    <p className="text-white font-medium">Copiar Link</p>
                    <p className="text-xs text-slate-400">{shortUrl}</p>
                  </div>
                </button>
                
                <button 
                  onClick={() => {
                    const msg = `Confira meus serviços: ${publicUrl}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                  }}
                  className="flex items-center gap-3 p-4 bg-slate-800 rounded-lg hover:bg-slate-700 transition-all"
                >
                  <MessageSquare className="text-green-400" size={24} />
                  <div className="text-left">
                    <p className="text-white font-medium">Compartilhar</p>
                    <p className="text-xs text-slate-400">Via WhatsApp</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Appointments */}
              <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white">Próximos Agendamentos</h3>
                  <button onClick={() => setActiveTab('appointments')} className="text-amber-400 text-sm hover:underline">
                    Ver todos
                  </button>
                </div>
                {appointments.filter(a => a.status === 'pending').slice(0, 3).length > 0 ? (
                  <div className="space-y-3">
                    {appointments.filter(a => a.status === 'pending').slice(0, 3).map((appt, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{appt.name}</p>
                          <p className="text-xs text-slate-400">{appt.service?.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-amber-400 text-sm">{appt.date}</p>
                          <p className="text-xs text-slate-500">{appt.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">Nenhum agendamento pendente</p>
                )}
              </div>

              {/* Recent Quotes */}
              <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white">Orçamentos Recentes</h3>
                  <button onClick={() => setActiveTab('quotes')} className="text-amber-400 text-sm hover:underline">
                    Ver todos
                  </button>
                </div>
                {quotes.slice(0, 3).length > 0 ? (
                  <div className="space-y-3">
                    {quotes.slice(0, 3).map((quote, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{quote.name}</p>
                          <p className="text-xs text-slate-400">{quote.service?.name || 'Serviço geral'}</p>
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => {
                            const phone = quote.phone?.replace(/\D/g, '');
                            window.open(`https://wa.me/55${phone}`, '_blank');
                          }}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <MessageSquare size={14} />
                          Responder
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">Nenhum orçamento recebido</p>
                )}
              </div>
            </div>

            {/* Monthly Report */}
            <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 rounded-xl p-6 border border-amber-500/20">
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="text-amber-400" size={24} />
                <h3 className="font-semibold text-white">Relatório Mensal - {new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</h3>
              </div>
              <div className="grid sm:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold text-white">{monthlyAppointments.length}</p>
                  <p className="text-sm text-slate-400">Agendamentos</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{monthlyQuotes.length}</p>
                  <p className="text-sm text-slate-400">Orçamentos</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-400">R$ {monthlyRevenue.toFixed(0)}</p>
                  <p className="text-sm text-slate-400">Faturamento</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-400">{stats?.total_testimonials || 0}</p>
                  <p className="text-sm text-slate-400">Avaliações</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Agendamentos</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="border-slate-700 text-slate-300">
                  <Filter size={16} />
                  Filtrar
                </Button>
              </div>
            </div>

            {appointments.length > 0 ? (
              <div className="space-y-3">
                {appointments.map((appt, i) => (
                  <div key={i} className="bg-slate-900 rounded-xl p-4 border border-slate-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          appt.status === 'completed' ? 'bg-green-500/20' :
                          appt.status === 'cancelled' ? 'bg-red-500/20' : 'bg-amber-500/20'
                        }`}>
                          {appt.status === 'completed' ? <Check className="text-green-400" /> :
                           appt.status === 'cancelled' ? <X className="text-red-400" /> :
                           <Clock className="text-amber-400" />}
                        </div>
                        <div>
                          <p className="text-white font-medium">{appt.name}</p>
                          <p className="text-sm text-slate-400">{appt.service?.name}</p>
                          <p className="text-xs text-slate-500">{appt.phone}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-400">{appt.date}</p>
                        <p className="text-sm text-slate-400">{appt.time}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          appt.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          appt.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          {appt.status === 'completed' ? 'Concluído' :
                           appt.status === 'cancelled' ? 'Cancelado' : 'Pendente'}
                        </span>
                      </div>
                    </div>
                    {appt.status === 'pending' && (
                      <div className="flex gap-2 mt-4 pt-4 border-t border-slate-800">
                        <Button 
                          size="sm" 
                          onClick={() => {
                            const phone = appt.phone?.replace(/\D/g, '');
                            const msg = `Olá ${appt.name}! Confirmando seu agendamento para ${appt.date} às ${appt.time}.`;
                            window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(msg)}`, '_blank');
                          }}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <MessageSquare size={14} />
                          Confirmar
                        </Button>
                        <Button size="sm" variant="outline" className="border-slate-700 text-slate-300">
                          Remarcar
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-slate-900 rounded-xl border border-slate-800">
                <Calendar size={48} className="mx-auto mb-4 text-slate-600" />
                <p className="text-slate-400">Nenhum agendamento ainda</p>
              </div>
            )}
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Orçamentos</h2>
            </div>

            {quotes.length > 0 ? (
              <div className="space-y-3">
                {quotes.map((quote, i) => (
                  <div key={i} className="bg-slate-900 rounded-xl p-4 border border-slate-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{quote.name}</p>
                        <p className="text-sm text-slate-400">{quote.service?.name || 'Serviço geral'}</p>
                        <p className="text-xs text-slate-500">{quote.phone}</p>
                        {quote.message && (
                          <p className="text-sm text-slate-400 mt-2 italic">"{quote.message}"</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => {
                            const phone = quote.phone?.replace(/\D/g, '');
                            window.open(`https://wa.me/55${phone}`, '_blank');
                          }}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <MessageSquare size={14} />
                          Responder
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => {
                            const phone = quote.phone?.replace(/\D/g, '');
                            window.open(`tel:+55${phone}`);
                          }}
                          className="border-slate-700 text-slate-300"
                        >
                          <Phone size={14} />
                          Ligar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-slate-900 rounded-xl border border-slate-800">
                <FileText size={48} className="mx-auto mb-4 text-slate-600" />
                <p className="text-slate-400">Nenhum orçamento recebido ainda</p>
              </div>
            )}
          </div>
        )}

        {/* Share Tab */}
        {activeTab === 'share' && (
          <div className="space-y-6">
            {/* Links */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Link2 className="text-amber-400" />
                Seus Links
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400 text-sm">Link completo</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="text"
                      value={publicUrl}
                      readOnly
                      className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    />
                    <Button onClick={() => copyLink(publicUrl)} variant="outline" className="border-slate-700">
                      {copied ? <Check size={18} /> : <Copy size={18} />}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-400 text-sm flex items-center gap-2">
                    Link curto
                    <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">Pro</span>
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="text"
                      value={shortUrl}
                      readOnly
                      className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-amber-400 font-medium"
                    />
                    <Button onClick={() => copyLink(`https://${shortUrl}`)} variant="outline" className="border-slate-700">
                      {copied ? <Check size={18} /> : <Copy size={18} />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <QrCode className="text-amber-400" />
                QR Code
                <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">Pro</span>
              </h3>
              
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div ref={qrRef} className="bg-white p-4 rounded-xl">
                  <QRCodeSVG 
                    value={publicUrl}
                    size={180}
                    level="H"
                    includeMargin={false}
                  />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-slate-400 mb-4">
                    Use este QR Code em seu cartão de visita, panfletos ou na vitrine do seu negócio.
                  </p>
                  <Button onClick={downloadQR} className="bg-amber-500 hover:bg-amber-600">
                    <Download size={18} />
                    Baixar QR Code
                  </Button>
                </div>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
              <h3 className="font-semibold text-white mb-4">Compartilhar</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <Button 
                  onClick={() => {
                    const msg = `Confira meus serviços profissionais: ${publicUrl}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                  }}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <MessageSquare size={18} />
                  WhatsApp
                </Button>
                <Button 
                  onClick={() => {
                    navigator.clipboard.writeText(publicUrl);
                    alert('Link copiado! Cole na sua bio do Instagram.');
                  }}
                  variant="outline"
                  className="border-pink-500/50 text-pink-400 hover:bg-pink-500/10"
                >
                  Instagram Bio
                </Button>
                <Button 
                  onClick={() => copyLink(publicUrl)}
                  variant="outline"
                  className="border-slate-700 text-slate-300"
                >
                  <Copy size={18} />
                  Copiar Link
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-2xl max-w-sm w-full p-6 text-center">
            <h3 className="text-xl font-bold text-white mb-6">Seu QR Code</h3>
            
            <div ref={qrRef} className="bg-white p-6 rounded-xl inline-block mb-6">
              <QRCodeSVG 
                value={publicUrl}
                size={200}
                level="H"
                includeMargin={false}
              />
            </div>
            
            <p className="text-slate-400 text-sm mb-6">{shortUrl}</p>
            
            <div className="flex gap-3">
              <Button onClick={downloadQR} className="flex-1 bg-amber-500 hover:bg-amber-600">
                <Download size={18} />
                Baixar
              </Button>
              <Button onClick={() => setShowQRModal(false)} variant="outline" className="flex-1 border-slate-700 text-slate-300">
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Label component if not imported
const Label = ({ children, className = '' }) => (
  <label className={`block text-sm font-medium ${className}`}>{children}</label>
);

export default ProPainel;
